import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import { leaveFormTo } from "../../utils/navigation";
import Typography from "@mui/material/Typography";
import { Banner } from "../../components/mui";
import EntityForm from "../../components/design/EntityForm";
import { formConfig, formInitialValues, FORM_TITLES } from "../../resources/forms";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPostForm } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { jwtEncode } from "../../utils/jwt";
import {
  LABEL_TO_PRIO,
  LABEL_TO_STATUS,
  LABEL_TO_TYPE,
  PRIO_CODE,
  STATUS_CODE,
  TYPE_TO_ISSUE_KEY,
  memberRoleLabel,
} from "../../design/domain";

/**
 * 新增工項（設計稿 #screen-task-new，el-pm-template_4.html:1124-1189）
 *
 * 舊表單 11 個欄位一次攤開，使用者第一眼看到的是一面表格而不是一個問題。
 * 這裡只問四題 ——「要做什麼／誰負責／什麼時候交／哪一種」—— 其餘收進
 * 「補充資料」的 <details>（分組定義在 resources/forms.js，欄位一個都沒有刪），
 * 再把這四題拆成兩／三步一次問一兩格（見下面「為什麼改成分步驟」）。
 *
 * 版型、必填防呆、收合區、步驟指示器與整頁唯一的主要按鈕都由 EntityForm 提供，
 * 這一支只負責三件事：問出專案、把下拉的中文轉回後端的代碼、送出後帶去那件工項。
 *
 * ── 為什麼多一個「要加在哪個專案」欄位 ──
 * 設計稿的這張畫面是從專案詳情進來的，專案是已知的。但 /Task/New 是一個可以直接
 * 貼給別人的網址，沒有專案就建不出工項（後端 ProjectId 必填），所以網址沒帶
 * projectId 時，第一欄先問專案 —— 它同時決定了「誰負責」與「屬於哪個里程碑」
 * 能選哪些人、哪些里程碑，所以必須排在最前面。
 * 這個欄位只存在於這一頁，不加進 forms.js 的共用設定：專案詳情頁用同一份
 * formConfig("items")，那裡的專案是已知的，多一格只會多一次誤選。
 */

/**
 * ── 為什麼改成分步驟（使用者原話：「可以用 STEP 新增 KEY 欄位嗎 必填選填清楚說明」）──
 *
 * 一張卡片四欄攤開，加上底下的「補充資料」，第一眼看到的還是一面表單。
 * 分步驟之後一次只問一兩題，而且每按一次「下一步」就驗這一步的必填 ——
 * 錯誤在當下就出現在那一欄底下，不會等到最後一次送出才一起爆出來。
 *
 * ── 步驟怎麼分 ──
 * 分法跟著「後面的選項要靠前面的答案才生得出來」這條依賴走，不是平均切：
 *   · 專案決定了「誰負責」與「屬於哪個里程碑」能選誰、能選哪些里程碑，
 *     所以網址沒帶 projectId 時，它必須自己佔在第一步、跟標題放在一起
 *     （標題是這件事的本體，兩題湊一步才不會變成「一步一格」的空轉）。
 *   · 「誰負責」與「什麼時候要交」是同一件事的兩面（承諾給誰、承諾到哪天），
 *     放同一步。
 *   · 「這是哪一種」只是分類，跟「補充資料」一起收在最後一步 ——
 *     最後一步本來就該是「還有什麼要補的」，而且補充資料那一區不算短，
 *     它一個人就撐得起一步。
 *
 * 網址已經帶了專案時少一題，就不該硬湊三步（走完三步卻只填四格很煩），
 * 所以那種情況併成兩步：標題／負責人／截止日一步，分類＋補充資料一步。
 * 每一步都在 2~3 個欄位之間，不會出現「一步只有一格」或「一步塞五格」。
 */

/**
 * 第一欄（選項在 render 時才補上，選項是後端來的真專案，不是寫死的名單）。
 * hint 說的是「下一步」不是「下面」—— 分步驟之後負責人與里程碑都不在這一屏；
 * 用語統一叫「里程碑」，與後端資料模型（Phase／里程碑）一致。
 */
const PROJECT_FIELD = {
  key: "__project",
  label: "所屬專案",
  type: "select",
  req: true,
  hint: "選擇專案後，下一步才會列出可指派的成員與可歸屬的里程碑。",
};

/** 後端 ReqPostTaskValidator：Title 最多 300 字元。這裡先擋，訊息才進得了那一欄底下 */
const TITLE_MAX = 300;

/**
 * 最後一步的說明。
 *
 * 不能寫成「以下全部都可以留空，建好之後隨時能改」—— 這一步唯一的主要欄位
 * 「這是哪一種」建好之後就改不了：PUT /Task 的 ReqPutTask 沒有型別欄位，
 * 所以 Task/Detail 的編輯表單直接把它濾掉（Task/Detail.js 有完整說明），
 * 唯一的補救是刪掉重建。收合區裡的「代碼」也一樣（那格是系統發號的唯讀欄位）。
 * 留空是真的可以，但「隨時能改」對這一格是假話，照實寫。
 */
const LAST_STEP_HINT =
  "以下欄位皆可留空，但類型於建立後無法修改（如需變更只能刪除後重新建立），建議現在選定；其餘欄位日後仍可於工項中編輯。";

function TaskNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useApp();

  /* 從工項清單或專案詳情帶過來的專案（?projectId=12）。有帶就不必再問一次 */
  const fixedProjectId = useMemo(() => {
    const v = searchParams.get("projectId") || searchParams.get("project") || "";
    const n = Number(v);
    return n > 0 ? n : 0;
  }, [searchParams]);

  const [projects, setProjects] = useState([]);
  /*
   * 專案清單的載入結果要分「還在讀／讀到了／讀不到」三種：
   * 這一頁的第一題就是「要加在哪個專案」，而且它是必填。清單是空的時候
   * 使用者會卡在第一步按不過去（下拉裡一個選項都沒有），畫面上非得講出原因不可。
   */
  const [projectsState, setProjectsState] = useState("loading");
  /** 專案成員：[{ label: "林怡君（開發）", account, name }]。label 才是下拉的值 */
  const [members, setMembers] = useState([]);
  /** 里程碑：要 id 才送得出 PhaseId，所以存整包而不是只存名稱 */
  const [phases, setPhases] = useState([]);
  const [sending, setSending] = useState(false);
  /*
   * 欄位層級的錯誤 { key: 整句訊息 }。交給 EntityForm 印在那一欄底下，
   * 它也會把人帶回那一欄所在的那一步 —— 分步驟之後出問題的欄位常常在兩步之前，
   * 只丟一個幾秒就消失的 toast，使用者會停在最後一步、畫面上一格紅的都沒有。
   */
  const [fieldErrors, setFieldErrors] = useState({});
  /* 整筆送出失敗的原因（權限不足、專案已被刪、500…）。這種錯不屬於任何一格，
     但也不能只用 toast：它會自己消失，使用者回頭就不知道剛剛發生什麼事 */
  const [submitError, setSubmitError] = useState("");

  /*
   * 初值在 useState 的 initializer 裡算，不要用模組層級常數 ——
   * 開始日預設「今天」，模組常數只會在載入那一刻算一次，分頁開著跨過午夜就停在昨天。
   */
  const [values, setValues] = useState(() => ({
    ...formInitialValues(formConfig("items")),
    __project: "",
  }));

  /* 選到的專案 id：網址帶的優先，其次才是使用者在第一欄選的 */
  const projectId = useMemo(() => {
    if (fixedProjectId > 0) return fixedProjectId;
    const hit = projects.find((p) => p.name === values.__project);
    return hit ? Number(hit.id) : 0;
  }, [fixedProjectId, projects, values.__project]);

  const projectName = useMemo(() => {
    const hit = projects.find((p) => Number(p.id) === projectId);
    return hit ? hit.name : "";
  }, [projects, projectId]);

  /*
   * 專案清單一律抓：即使網址已經帶了 projectId，副標也要寫出「在『新 WEBPOS』裡
   * 加一件事」，那個名字必須是真的，不能拿網址上的數字充數（硬規則 10）。
   */
  useEffect(() => {
    let alive = true;
    apiGet("/Project/List", {
      Data: jwtEncode({ Page: 0, PageCount: 0, OrderKey: "id", OrderDirect: "DESC" }),
    })
      .then((res) => {
        if (!alive) return;
        const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
        setProjects(
          arr
            .map((p) => ({ id: p.id ?? p.Id, name: p.name || p.Name || "" }))
            .filter((p) => p.id != null && p.name)
        );
        setProjectsState("ready");
      })
      /* 錯誤不能整個吞掉：吞掉之後畫面跟「你一個專案都沒有」長得一模一樣，
         使用者會以為自己真的沒有專案，而不是這一次讀取失敗 */
      .catch(() => {
        if (alive) setProjectsState("failed");
      });
    return () => {
      alive = false;
    };
  }, []);

  /*
   * 成員與里程碑跟著專案換。走 utils/api 直呼叫而不是 redux：
   * 這兩份清單是這張表單自己的下拉選項，寫進 state.Project.Members / state.Phase.List
   * 會把專案詳情頁正在用的那一份蓋掉（同一個 key），使用者切回去就看到別的專案的成員。
   */
  useEffect(() => {
    if (!projectId) {
      setMembers([]);
      setPhases([]);
      return undefined;
    }
    let alive = true;
    apiGet("/Project/Member/List", { Data: jwtEncode({ ProjectId: projectId }) })
      .then((res) => {
        if (!alive) return;
        const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
        setMembers(
          arr
            .map((m) => {
              const account = m.account || m.Account || "";
              const name = m.name || m.Name || account;
              const role = memberRoleLabel(m.role ?? m.Role);
              /* 選項文字帶角色（設計稿 L1141）：同名同姓分得出來，
                 也看得出這件事是丟給開發還是測試 */
              return { account, name, label: role ? `${name}（${role}）` : name };
            })
            .filter((m) => m.name)
        );
      })
      .catch(() => {
        if (alive) setMembers([]);
      });
    apiGet(`/Phase/List/${projectId}`)
      .then((res) => {
        if (!alive) return;
        const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
        setPhases(
          arr.map((p) => ({ id: p.id ?? p.Id, name: p.name || p.Name || "" })).filter((p) => p.name)
        );
      })
      .catch(() => {
        if (alive) setPhases([]);
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  /* 換專案就把「誰負責」「屬於哪個里程碑」清掉：上一個專案的人名留在欄位裡，
     送出時會對不到帳號，就變成一件沒有人負責的工項 */
  useEffect(() => {
    setValues((prev) => ({ ...prev, assignee: "", phase: "" }));
  }, [projectId]);

  const fields = useMemo(() => {
    const base = formConfig(
      "items",
      members.map((m) => m.label),
      phases.map((p) => p.name)
    );
    const adjusted = base.map((f) => {
      /* 還沒選專案時這兩欄是選不出東西的下拉 —— 說明文字要講出原因，
         不然使用者會以為系統壞了 */
      /* 分步驟之後專案是在第一步問的，所以說的是「上一步」不是「上面」 */
      if (!projectId && f.key === "assignee") {
        return { ...f, hint: "請先於上一步選擇專案，此處才會列出可指派的成員。" };
      }
      if (!projectId && f.key === "phase") {
        return { ...f, hint: "請先於上一步選擇專案，此處才會列出里程碑。" };
      }
      if (projectId && f.key === "phase" && phases.length === 0) {
        return { ...f, hint: "此專案尚未建立里程碑，可至「時程 → 新增里程碑」建立後再選擇；也可先留空。" };
      }
      return f;
    });
    if (fixedProjectId > 0) return adjusted;
    /* 下拉裡一個選項都沒有的時候，說明文字要改講原因 —— 不然使用者只會看到
       一個「必填、卻選不出東西」的下拉，然後一直按「下一步」 */
    let projectHint = PROJECT_FIELD.hint;
    if (projectsState === "failed") projectHint = "專案清單載入失敗，因此選單沒有選項。請重新整理頁面後再試一次。";
    else if (projectsState === "ready" && projects.length === 0) projectHint = "您目前沒有可選擇的專案，因此選單沒有選項。";
    return [{ ...PROJECT_FIELD, hint: projectHint, options: projects.map((p) => p.name) }, ...adjusted];
  }, [members, phases, projectId, fixedProjectId, projects, projectsState]);

  /*
   * 第一步就走不下去的兩種情況要在表單上面講清楚，並且給一條出路：
   * 「要加在哪個專案」是必填，清單空的時候使用者按幾次「下一步」都只會被退回同一格。
   */
  const projectBlock = useMemo(() => {
    if (fixedProjectId > 0 || projects.length > 0) return "";
    if (projectsState === "failed") {
      return "專案清單載入失敗，「所屬專案」沒有選項，因此無法建立工項。請重新整理頁面後再試一次；若持續失敗，請聯絡系統管理員。";
    }
    if (projectsState === "ready") {
      return "您尚未加入任何專案。工項必須隸屬於專案，因此目前無法建立工項。請聯絡專案負責人將您加入專案，或至「專案」新增專案後，再返回新增工項。";
    }
    return "";
  }, [fixedProjectId, projects, projectsState]);

  /*
   * 步驟定義（理由見檔案上方）。只點名主要欄位 —— extra: true 的那幾欄
   * 由 EntityForm 自己收在最後一步的「補充資料」裡。
   * 標題用白話問句，跟 forms.js 既有的 label／hint 同一個語氣。
   */
  const steps = useMemo(() => {
    if (fixedProjectId > 0) {
      return [
        {
          title: "工項內容、負責人與里程碑",
          hint: "標題與負責人為必填。截止日可暫不填寫，但填寫後負責人才會在每日提醒信中收到到期提醒。",
          keys: ["title", "assignee", "due_date", "phase"],
        },
        { title: "類型與其他資訊", hint: LAST_STEP_HINT, keys: ["type"] },
      ];
    }
    return [
      {
        title: "所屬專案與工項內容",
        hint: "兩欄皆為必填。選擇專案後，下一步才會列出可指派的成員。",
        keys: ["__project", "title"],
      },
      {
        title: "負責人、截止日與里程碑",
        hint: "負責人為必填。截止日可暫不填寫，但填寫後負責人才會在每日提醒信中收到到期提醒。",
        keys: ["assignee", "due_date", "phase"],
      },
      { title: "類型與其他資訊", hint: LAST_STEP_HINT, keys: ["type"] },
    ];
  }, [fixedProjectId]);

  const handleChange = (key, val) => {
    /* 改了哪一格就把那一格的錯誤收掉，紅字不要黏在已經改過的欄位上 */
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  /* 返回：從專案進來的回那個專案（工項分頁），直接開網址進來的回個人工項。
     原本回 /Tasks，那一頁已經拿掉了 */
  const back = () => navigate(fixedProjectId > 0 ? `/Project/Detail/${fixedProjectId}` : "/MyTask");

  /**
   * 建好之後要帶去「那一件」工項，但後端 POST /Task 回的 data 是空字串（沒有新 id），
   * 所以回頭在該專案的工項清單裡找：同標題、id 最大的那一筆就是剛剛建的。
   * 找不到就帶回清單並說清楚 —— 寧可少跳一步，也不要跳到一個猜出來的 id。
   */
  const findNewTaskId = async (title) => {
    try {
      const res = await apiGet(`/Task/List/${projectId}`, {
        Data: jwtEncode({ Page: 1, PageCount: 50, OrderKey: "id", OrderDirect: "DESC" }),
      });
      const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
      const hit = arr
        .filter((t) => String(t.title || t.Title || "").trim() === title)
        .map((t) => Number(t.id ?? t.Id))
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => b - a)[0];
      return hit || 0;
    } catch (e) {
      return 0;
    }
  };

  const handleSubmit = async () => {
    if (sending) return;

    const title = String(values.title || "").trim();
    /* EntityForm 已經擋過一輪必填（訊息就寫在那一欄底下），
       這裡是與後端 validator 對齊的第二道，不要拿掉 */
    if (!projectId) {
      toast("請選擇所屬專案", "error");
      return;
    }
    if (!title) {
      toast("請填寫工項標題", "error");
      return;
    }
    /*
     * 標題長度也在這裡擋：後端 ReqPostTaskValidator 是 300 字元，
     * 輸入框本身不擋字數。等後端退回來的話，使用者已經走到最後一步，
     * 而出問題的那一格在第一步 —— 先擋下來才寫得回那一欄底下。
     */
    if (title.length > TITLE_MAX) {
      setFieldErrors({ title: `標題最多 ${TITLE_MAX} 個字，目前為 ${title.length} 個字，請縮短後再送出。` });
      return;
    }
    setFieldErrors({});
    setSubmitError("");

    const typeKeyName = LABEL_TO_TYPE[values.type];
    const member = members.find((m) => m.label === values.assignee);
    const phase = phases.find((p) => p.name === values.phase);
    const payload = {
      ProjectId: projectId,
      Title: title,
      Description: values.description || "",
      /* 代碼由後端依「專案代碼-流水號」發號（TaskService.PostTask 會蓋掉送上去的值） */
      IssueKey: "",
      IssueTypeKey: typeKeyName ? TYPE_TO_ISSUE_KEY[typeKeyName] : "",
      /* 留空＝刻意不分里程碑，送 null（與專案詳情頁的送法一致） */
      PhaseId: phase ? Number(phase.id) : null,
      Assignee: member ? member.account : "",
      Status: STATUS_CODE[LABEL_TO_STATUS[values.status]] ?? 0,
      Priority: PRIO_CODE[LABEL_TO_PRIO[values.priority]] ?? 1,
      StartDate: values.start_date || "",
      DueDate: values.due_date || "",
      EstimatedHours: values.estimated_hours || "",
    };

    setSending(true);
    try {
      // 後端 [FromForm] ReqPost 只認 data 欄位，內容需為 JWT 封裝的 payload
      await apiPostForm("/Task", { data: jwtEncode(payload) });
      const id = await findNewTaskId(title);
      if (id) {
        toast("已建立工項");
        /* 表單的幾個步驟從歷史裡退掉再換成工項頁：在工項頁按返回才不會掉回送出過的表單 */
        leaveFormTo(navigate, `/Task/${id}`);
      } else {
        /* 帶回那個專案的工項分頁：剛建的那件就在清單裡，比丟到個人工項好找
           （如果負責人不是自己，它根本不會出現在個人工項） */
        toast("已建立工項，但暫時無法取得工項編號，將返回專案的工項清單");
        leaveFormTo(navigate, projectId ? `/Project/Detail/${projectId}` : "/MyTask");
      }
    } catch (e) {
      // 後端把失敗原因放在 res_msg（例如「新增失敗: 專案不存在」），優先顯示它
      const msg = apiErrorMessage(e, "建立工項失敗");
      toast(msg, "error");
      /* toast 幾秒後就沒了，同一句話再留一份在畫面上（硬規則 4：重要後果寫成提示條） */
      setSubmitError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* 第一步就走不下去的原因寫成提示條，不是按鈕底下的灰字小字（硬規則 4） */}
      {projectBlock && (
        <Banner kind="attention" sx={{ maxWidth: 640, mb: 2 }}>
          <strong>目前無法建立工項　</strong>
          {projectBlock}
        </Banner>
      )}
      <EntityForm
        title={FORM_TITLES.items}
        /* 副標只有在真的知道專案名字時才寫（設計稿 L1128：「在『新 WEBPOS』裡加一件事。」） */
        subtitle={projectName ? `所屬專案：${projectName}` : ""}
        fields={fields}
        /* 只有這一個入口打開分步驟：編輯既有工項（Task/Detail）不傳 steps，
           使用者常常只想改一格，不該被逼著走完整個精靈 */
        steps={steps}
        values={values}
        onChange={handleChange}
        onBack={back}
        onSubmit={handleSubmit}
        /* 欄位層級的錯誤：EntityForm 會印在那一欄底下，並把人帶回那一欄所在的那一步 */
        errors={fieldErrors}
        /* 主鈕寫出下一步，不是寫「建立」—— 按下去會發生什麼事，按之前就該知道 */
        submitText="建立並檢視工項"
        busy={sending}
      />
      {/*
        整筆送出失敗：toast 已經講過一次，但它幾秒後就消失，而使用者還停在最後一步。
        同一句話留一份在按鈕下方，並且講清楚「填的東西還在」，不然他只會重刷一次頁面。
      */}
      {submitError && (
        <Banner kind="attention" role="alert" sx={{ maxWidth: 640, mt: 2 }}>
          <strong>建立失敗　</strong>
          {submitError}
          　已填寫的內容仍保留，可按「上一步」返回修改，完成後再按一次「建立並檢視工項」。
        </Banner>
      )}
      {/*
        流程說明寫在卡片下方的次要小字。
        max-width 對齊 EntityForm 卡片的 640px，否則這句話會橫跨整個內容寬、跟卡片對不齊。
      */}
      {/*
        「通知」照實寫，理由同 Schedule/GoalNew.js:409-412：
        後端 TaskService.PostTask 建完工項只寫一筆 ActivityLog，然後呼叫
        NotificationService.NotifyProject —— 那是一次對 project_{id} 群組的 SignalR
        廣播，完全沒有讀 model.Assignee，也不落地、不寄信（api/ 底下沒有任何
        SMTP／MailMessage）。被指派的人當下沒開著系統就什麼都收不到，
        事後也補不回來（沒有 NotificationController，通知中心是空的）。
        寫「被指派的人也會收到通知」會讓 PM 不再私下講一聲，那件事就沒人知道。
      */}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640, mt: 2 }}>
        建立後將直接開啟該工項，不會返回列表；當下正在使用系統的專案成員會收到一則即時通知（不寄送電子郵件）。
      </Typography>
    </>
  );
}

export default TaskNew;
